import { PageCanvas, FormContent } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Errors } from "../requests/controls";
import { money } from "../jobs/types";
import {
  locationJobPaymentPostingsPath,
  locationJobAdvanceRequestsPath,
} from "@/routes";

export default function AdvanceForm({
  job_id,
  ledger_context = {},
  revision,
  entry_key,
  currency,
  total_minor,
  due_at,
  time_zone,
}: {
  job_id: string;
  ledger_context?: { page?: number; requests_page?: number };
  revision: string;
  entry_key: string;
  currency: string;
  total_minor: string;
  due_at: string;
  time_zone: string;
}) {
  const { auth, i18n, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const path = locationJobPaymentPostingsPath(
    auth.location.id,
    job_id,
    ledger_context,
  );
  return (
    <JobWorkspace
      primaryAction={false}
      breadcrumbs={[{ title: t("payments.title"), href: path }]}
    >
      <Head title={t("payments.request")} />
      <PageCanvas padded={false}>
        <FormContent>
          <h2 className="text-page font-display">{t("payments.request")}</h2>
          <p className="text-muted-foreground text-sm">
            {t("payments.request_help")}
          </p>
          <p>
            {t("payments.total")}: {money(total_minor, currency, i18n.locale)}
          </p>
          <Form
            action={locationJobAdvanceRequestsPath(auth.location.id, job_id)}
            method="post"
          >
            {({ processing }) => (
              <div className="space-y-4">
                {Object.entries(ledger_context).map(([key, value]) => (
                  <input
                    key={key}
                    type="hidden"
                    name={`ledger[${key}]`}
                    value={value}
                  />
                ))}
                <Errors
                  targets={{
                    value: "advance-value",
                    due_at: "advance-due_at",
                    reason: "advance-reason",
                    kind: "advance-kind",
                  }}
                />
                <input
                  type="hidden"
                  name="advance_request[revision]"
                  value={revision}
                />
                <input
                  type="hidden"
                  name="advance_request[entry_key]"
                  value={entry_key}
                />
                <div className="space-y-2">
                  <Label htmlFor="advance-kind">
                    {t("payments.request_kind")}
                  </Label>
                  <Select
                    name="advance_request[kind]"
                    defaultValue="fixed"
                    items={{
                      fixed: t("payments.fixed"),
                      percentage: t("payments.percentage"),
                    }}
                  >
                    <SelectTrigger id="advance-kind" autoFocus>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["fixed", "percentage"].map((kind) => (
                        <SelectItem key={kind} value={kind}>
                          {t(`payments.${kind}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(["value", "due_at", "reason"] as const).map((name) => (
                  <div key={name} className="space-y-2">
                    <Label htmlFor={`advance-${name}`}>
                      {t(`payments.${name}`)}
                      {name === "value" ? ` (${currency} / %)` : ""}
                    </Label>
                    <Input
                      id={`advance-${name}`}
                      name={`advance_request[${name}]`}
                      defaultValue={name === "due_at" ? due_at : undefined}
                      aria-invalid={!!errors?.[name]}
                      required
                      maxLength={name === "value" ? 25 : 500}
                      inputMode={name === "value" ? "decimal" : undefined}
                    />
                    {errors?.[name] && (
                      <p className="text-sm text-destructive">
                        {String(errors[name])}
                      </p>
                    )}
                  </div>
                ))}
                <p className="text-muted-foreground text-sm">
                  {t("payments.time_help", { zone: time_zone })}
                </p>
                <Button type="submit" disabled={processing}>
                  {t("payments.request")}
                </Button>
              </div>
            )}
          </Form>
          <Link className="block underline" href={path}>
            {t("inventory.cancel")}
          </Link>
        </FormContent>
      </PageCanvas>
    </JobWorkspace>
  );
}
